import { useState } from 'react';
import { Button, Dialog, Modal } from 'alurkerja-ui';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { MdCloudUpload } from 'react-icons/md';
import { UploadField } from '@/components/form/fields/UploadField';
import { FormContainer } from '@/components/form/FormContainer';
import { useForm } from 'react-hook-form';
import {
  IImportFileSchema,
  importFileSchema,
  TestCaseService,
} from '@/api/testCase';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';

type ImportErrorItem = {
  row: number | '-';
  column: string;
  message: string;
};

type ImportErrorResponse = {
  message?: string;
  errors: ImportErrorItem[];
};

type ImportTestCaseProps = {
  id?: string;
};

export const ImportTestCase: React.FC<ImportTestCaseProps> = ({ id }) => {
  const [importErrors, setImportErrors] = useState<ImportErrorItem[]>([]);
  const form = useForm<IImportFileSchema>({
    resolver: zodResolver(importFileSchema),
  });

  const { mutate: importMutate, isPending: isImportLoading } =
    TestCaseService.useImport(id!);

  const submitAction = (data: IImportFileSchema) => {
    // Reset error sebelum submit baru
    setImportErrors([]);

    importMutate(data, {
      onSuccess: () => {
        Dialog.success({
          title: 'Berhasil',
          description: 'Berhasil Import Test Case',
          callback: () => window.location.reload(),
        });
      },
      onError: error => {
        if (error instanceof AxiosError && error.response?.status === 422) {
          const responseData = error.response.data as ImportErrorResponse | undefined;

          // Jika ada error per baris dari backend
          if (responseData?.errors && responseData.errors.length > 0) {
            setImportErrors(responseData.errors);
            return;
          }

          // Fallback ke error string biasa (misal Duplicate Entity dari bawaan lama)
          Dialog.error({
            title: 'Gagal',
            description: responseData?.message || 'Duplicate case code detected',
          });
          return;
        }

        Dialog.error({
          title: 'Gagal',
          description: 'Gagal Import Test Case',
        });
      },
    });
  };

  return (
    <Modal
      title="Import Test Case"
      maxWidth={importErrors.length > 0 ? '5xl' : '3xl'}
      triggerButton={
        <OutlineButton icon={<MdCloudUpload />}>Import</OutlineButton>
      }
    >
      <div className="m-5">
        {importErrors.length > 0 ? (
          <div className="mt-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-red-500 font-bold text-lg">Preview Error Import Data</h3>
              <Button onClick={() => setImportErrors([])} size="small">
                Upload Ulang
              </Button>
            </div>
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="w-full text-sm text-left text-gray-700 border">
                <thead className="text-xs uppercase bg-red-100 text-red-700 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 border font-semibold">No</th>
                    <th className="px-4 py-3 border font-semibold">Pesan</th>
                    <th className="px-4 py-3 border font-semibold">Baris</th>
                    <th className="px-4 py-3 border font-semibold">Kolom</th>
                  </tr>
                </thead>
                <tbody>
                  {importErrors.map((err, idx) => (
                    <tr key={idx} className="bg-white border-b hover:bg-gray-50">
                      <td className="px-4 py-3 border">{idx + 1}</td>
                      <td className="px-4 py-3 border text-red-600 font-medium">
                        {err.message}
                      </td>
                      <td className="px-4 py-3 border font-bold">Baris {err.row}</td>
                      <td className="px-4 py-3 border">{err.column}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <FormContainer form={form} onSubmit={submitAction}>
            <UploadField
              name="file"
              label="Attachments"
              note="Allowed Formats: xlsx"
              type="file"
              allowedExtensions={['xlsx']}
              hasRequiredAsterisk
            />
            <Button className="mt-3" loading={isImportLoading}>
              Import
            </Button>
          </FormContainer>
        )}
      </div>
    </Modal>
  );
};